<template>
    <div>
        <div>
            <report-header
                :title="title"
                :isShowDateRange="true"
                :formatDateRangeTitle="dateRange"
            />
            <b-card id="report-card">
                <div id="report-body" class="w-100">
                    <!-- Charitable Fund Summary -->
                    <div>
                        <p class="item-name">
                            Summary
                        </p>
                        <QTable
                            :columns="summaryHeaders"
                            :data="summaryData"
                            :loading="isLoading"
                            :rows-per-page-options="[0]"
                            hide-bottom
                        >
                            <template
                                v-slot:bottom-row
                                v-if="
                                    summaryData.length == 0 &&
                                    !isLoading &&
                                    !isError
                                "
                            >
                                <q-tr>
                                    <q-td colspan="6"
                                        >No records available</q-td
                                    >
                                </q-tr>
                            </template>
                            <template v-slot:body-cell-name="props">
                                <q-td :props="props">
                                    <a
                                        v-if="findFundInDetails(props.row.id)"
                                        href="#"
                                        v-scroll-to="`#table_${props.row.id}`"
                                    >
                                        {{ props.row.name }}
                                    </a>
                                    <span class="text-link" v-else>{{
                                        props.row.name
                                    }}</span>
                                </q-td>
                            </template>
                        </QTable>
                    </div>

                    <!-- Charitable Fund Details -->
                    <span v-for="data in detailsData" :key="data.id">
                        <div
                            v-if="data.transactions.length > 0"
                            :id="'table_' + data.id"
                        >
                            <p class="item-name">
                                <strong>{{ data.name }}</strong>
                            </p>
                            <QTable
                                :columns="transactionHeaders"
                                :data="data.transactions"
                                :loading="data.isLoading"
                                :rows-per-page-options="[0]"
                                hide-bottom
                            >
                                <template v-slot:body-cell-client="props">
                                    <q-td :props="props">
                                        <client-name
                                            :use-router="USE_CLIENT_MODULE_VUE"
                                            :client="
                                                generateClientObject(props.row)
                                            "
                                        />
                                    </q-td>
                                </template>
                                <template v-slot:body-cell-reference="props">
                                    <q-td :props="props">
                                        <a
                                            class="text-capitalize"
                                            v-if="
                                                props.row.reference
                                                    .toLowerCase()
                                                    .includes('invoice')
                                            "
                                            :href="
                                                env.APPS +
                                                'billing/invoice/view/' +
                                                props.row.reference_encoded_id
                                            "
                                        >
                                            {{ props.row.reference }}
                                        </a>
                                        <span v-else>
                                            {{ props.row.reference }}
                                        </span>
                                    </q-td>
                                </template>
                            </QTable>
                        </div>
                    </span>
                </div>
            </b-card>
        </div>
    </div>
</template>
<script>
import { QTable } from "quasar";
import Report from "@/mixins/report";
import ReportService from "@/services/report";
import ClientName from "@/components/commons/client-name";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";
import { forEach, sortBy, find, findIndex } from "lodash";

export default {
    mixins: [Report],
    components: {
        QTable,
        ClientName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("week"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            isLoading: true,
            isError: false,
            summaryData: [],
            detailsData: [],
            title: "Charitable Fund Summary",
            dateRange: "",
            perPage: 30,
            summaryHeaders: [
                {
                    name: "name",
                    label: "Charitable Fund",
                    field: "name",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "created",
                    label: "Created",
                    field: "created",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "start_balance",
                    label: "Starting Balance",
                    field: "start_balance",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "deposits",
                    label: "Deposits",
                    field: "deposits",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "payments",
                    label: "Payment",
                    field: "payments",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "ending_balance",
                    label: "Ending Balance",
                    field: "ending_balance",
                    align: "left",
                    style: "white-space: nowrap"
                }
            ],
            transactionHeaders: [
                {
                    name: "date",
                    label: "Date",
                    field: "date",
                    align: "left",
                    style: "width: 10%;",
                    classes: "align-baseline"
                },
                {
                    name: "type",
                    label: "Type",
                    field: "type",
                    align: "left",
                    style: "width: 10%;",
                    classes: "text-capitalize align-baseline"
                },
                {
                    name: "client",
                    label: "Client",
                    field: "client",
                    align: "left",
                    style: "width: 18%;",
                    classes: "text-capitalize align-baseline"
                },
                {
                    name: "reference",
                    label: "Reference",
                    field: "reference",
                    align: "left",
                    style: "width: 12%;",
                    classes: "text-capitalize align-baseline"
                },
                {
                    name: "amount",
                    label: "Amount",
                    field: "amount",
                    align: "left",
                    style: "width: 12%;",
                    classes: "align-baseline"
                }
            ]
        };
    },
    computed: {},
    async mounted() {
        let self = this;

        self.data.isShowDateRange = true;
        self.data.datePickerOptionDefault = 1;
        await self._getSetting();
        self.perPage = self.RECORD_PER_PAGE;
        self.data.start = self.getStartDate("week");
        self.data.end = self.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);
        self.init();

        self.$root.$on("report-filter-date-range:refresh", self.prepareData);
    },
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.prepareData);
    },
    methods: {
        prepareData(params) {
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
                this.data.start = params.start;
                this.data.end = params.end;
            }
            this.init();
        },
        async init() {
            let self = this;
            self.isLoading = true;
            self.isError = false;
            self.toggleOptions(false);
            self.detailsData = [];
            self.summaryData = [];
            self.dateRange = self.getDateRange(self.data.from, self.data.to);
            await ReportService.getReport(CREPORT.TYPE.CHARITABLE_FUND, {
                from: self.data.from,
                to: self.data.to
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.dateRange = self.getDateRange(
                            res.resource.meta.from,
                            res.resource.meta.to
                        );
                        self.dynamicPageTitle(self.title);
                        self.summaryData = sortBy(
                            res.resource.results.summary,
                            "name"
                        );
                        if (self.summaryData.length > 0) {
                            forEach(self.summaryData, fund => {
                                self.detailsData.push({
                                    isLoading: true,
                                    id: fund.id,
                                    transactions: [],
                                    name: fund.name
                                });
                                self.fetchDataDetails(fund.id);
                            });
                        } else {
                            self.endFetching();
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    this.endFetching();
                    self.isError = true;
                    console.error(e.message);
                    self._message("Error: Unable to get the report", "danger");
                });
        },
        async fetchDataDetails(fundId, page = 1) {
            let self = this;
            await ReportService.getReport(CREPORT.TYPE.CHARITABLE_FUND, {
                from: self.data.from,
                to: self.data.to,
                fund_id: fundId,
                perPage: this.perPage,
                page: page
            })
                .then(res => {
                    let fundIndex = findIndex(self.detailsData, {
                        id: fundId
                    });
                    if (res && res.message && res.message.code === 200) {
                        if (
                            Object.keys(res.resource.results.detail).length >
                                0 &&
                            res.resource.results.detail[fundId]
                        ) {
                            let fundDetails =
                                res.resource.results.detail[fundId];

                            forEach(fundDetails.data, transaction => {
                                self.detailsData[fundIndex].transactions.push(
                                    transaction
                                );
                            });

                            if (fundDetails.data.length == this.perPage) {
                                self.fetchDataDetails(fundId, page + 1);
                            } else {
                                self.endFetchDetails(fundIndex);
                            }
                        } else {
                            if (fundIndex !== -1) {
                                this.endFetchDetails(fundIndex);
                            }
                        }
                    } else {
                        this.endFetchDetails(fundIndex);
                        throw new Error(
                            "Error: Unable to get the fund details"
                        );
                    }
                })
                .catch(e => {
                    self.endFetchDetails(
                        findIndex(self.detailsData, {
                            id: fundId
                        })
                    );
                    console.error(e.message);
                    self.isError = true;
                    self._message(
                        "Error: Unable to get the fund details",
                        "danger"
                    );
                });
        },
        endFetchDetails(fundIndex) {
            if (fundIndex !== -1) {
                this.detailsData[fundIndex]["isLoading"] = false;
            }
            this.endFetching();
        },
        endFetching() {
            if (!find(this.detailsData, { isLoading: true })) {
                this.toggleOptions(true);
                this.isLoading = false;
            }
        },
        toggleOptions(value) {
            this.data.filterDateOption = value;
            this.data.exportOption = value;
            this.data.refreshOption = value;
        },
        generateClientObject(row) {
            const isDeleted = row.client.includes("deleted");
            return {
                id: row.client_encoded_id,
                name: row.client.replace("(deleted)", ""),
                encoded_id: isDeleted ? null : row.client_encoded_id,
                isactive: !isDeleted
            };
        },
        findFundInDetails(id) {
            let fund = find(this.detailsData, { id: id });
            return fund && fund.transactions.length > 0;
        }
    }
};
</script>
